import { ChevronDownIcon } from 'lucide-react'
import { useShallow } from 'zustand/shallow'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/atoms/accordion'
import Cell from '@/components/atoms/Cell'
import SectionHeader from '@/components/atoms/SectionHeader'
import PartHeader from '@/components/molecules/PartHeader'
import { useScopedI18n } from '@/locales/client'
import { useHubStore } from '@/stores/HubStore'
import { useMobileLayoutStore } from '@/stores/MobileLayoutStore'
import { cn } from '@/util/cn'

type HubIncludedKeys = 'amountLimitSensors' | 'amountLimitControls' | 'amountLimit12VOirt' | 'amountLimitPSUs'

function IncludedSection() {
  const { isMobile, activeMobileTab } = useMobileLayoutStore()
  const t = useScopedI18n('includedSection')
  const hubs = useHubStore(useShallow(
    state => state.hubs,
  ))
  const hubsIncluded = hubs
    .map(hub => Object.entries(hub)
      .filter(([key, value]) =>
        /limit/i.test(key)
        && typeof value === 'string'
        && !Number.isNaN(Number.parseInt(value)),
      ) as [HubIncludedKeys, string][],
    )

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1">
        <AccordionTrigger className="pb-1 pt-0 hover:no-underline">
          <SectionHeader
            title={t('sectionTitle')}
            className="flex flex-row items-center justify-start border-2 border-rsf-purple-light bg-rsf-white px-0 py-5 pr-8 shadow-none md:px-0 md:pr-8"
          >
            <ChevronDownIcon className="pointer-events-none size-5 shrink-0 translate-y-0.5 text-rsf-black transition-transform duration-200" />
          </SectionHeader>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-[2px]">
          {hubsIncluded && hubsIncluded.at(0)?.map(([key, value]) => (
            <div
              key={key}
              className={cn(
                'grid grid-cols-4 gap-[2px] tablet:grid-cols-5',
                isMobile && activeMobileTab === 'deluxe' && 'grid-cols-4',
              )}
            >
              <PartHeader
                className="h-[46px] items-center justify-start *:*:mb-0 *:items-center"
                part={{ partname: t(key) }}
                hideInfoAndPricing
              />

              {hubs.map(hub => (
                <Cell
                  key={hub.id + value}
                  className={cn(
                    'flex h-[46px] flex-col gap-0 p-2 font-sans text-xs font-semibold *:leading-none md:p-2 md:text-sm',
                    isMobile && activeMobileTab === 'deluxe' && 'col-span-2',
                  )}
                >
                  <p className="text-rsf-grey-dark">
                    {hub[key] as string}
                  </p>
                  {key === 'amountLimitSensors' && ['3', '4'].includes(hub.id) && (
                    <p className="text-balance break-keep text-center text-rsf-grey-dark">{t('reqExpander')}</p>
                  )}
                </Cell>
              ))}
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

export default IncludedSection
